import dayjs from 'dayjs';

import { ToCronExpressionInput } from '@/shared/types/transform.types';

const formatTimeOfDay = (hours: number, minutes: number): string =>
  `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

export const toCronExpression = (
  configuration: ToCronExpressionInput,
): string => {
  const { days, timeOfDay } = configuration;

  if (!days || !timeOfDay) {
    return 'cron(0 0 ? * * *)'; // Default cron expression
  }

  const [hours, minutes] = timeOfDay
    .split(':')
    .map((part) => parseInt(part, 10));

  const cronDays = days.sort((a, b) => a - b);
  const dayOfWeekExpression = cronDays.length > 0 ? cronDays.join(',') : '*';

  return `cron(${minutes} ${hours} ? * ${dayOfWeekExpression} *)`;
};

export const fromCronExpression = (
  cronExpression: string,
  expressionTimezone?: string,
): ToCronExpressionInput | null => {
  const cronRegex = /^cron\((\d+)\s+(\d+)\s+\?\s+\*\s+([1-7,*]+)\s+\*\)$/;
  const match = cronExpression.match(cronRegex);

  if (!match) {
    return null;
  }

  const [, minutesStr, hoursStr, daysOfWeekStr] = match;
  const originalHours = parseInt(hoursStr, 10);
  const originalMinutes = parseInt(minutesStr, 10);
  const timeOfDay = formatTimeOfDay(originalHours, originalMinutes);

  const isEveryDay = daysOfWeekStr === '*';
  const daysToProcess = isEveryDay
    ? [1, 2, 3, 4, 5, 6, 7]
    : daysOfWeekStr.split(',').map((day) => parseInt(day, 10));

  if (!expressionTimezone) {
    return {
      days: isEveryDay ? [] : [...daysToProcess].sort((a, b) => a - b),
      timeOfDay,
    };
  }

  const userTimeZone = dayjs.tz.guess();

  // Anchor the run in the schedule's own timezone before converting. Reading a
  // viewer-local instant into the schedule timezone first rolls the calendar
  // date whenever the zones straddle midnight, which shifts the weekday.
  const weekStartInSourceZone = dayjs().tz(expressionTimezone).startOf('week');

  const toUserTime = (dayOfWeek: number) => {
    const dateInSourceZone = weekStartInSourceZone
      .add(dayOfWeek - 1, 'day')
      .format('YYYY-MM-DD');

    return dayjs
      .tz(`${dateInSourceZone} ${timeOfDay}`, expressionTimezone)
      .tz(userTimeZone);
  };

  // For each day of the week, calculate how it would be in the user's timezone
  const adjustedDays: number[] = [];
  for (const originalDay of daysToProcess) {
    const newDayOfWeek = toUserTime(originalDay).day() + 1;

    if (!adjustedDays.includes(newDayOfWeek)) {
      adjustedDays.push(newDayOfWeek);
    }
  }

  // Use the first day to calculate the final time
  const userTime = toUserTime(daysToProcess[0]);

  return {
    days: isEveryDay ? [] : adjustedDays.sort((a, b) => a - b),
    timeOfDay: formatTimeOfDay(userTime.hour(), userTime.minute()),
  };
};

export const snakeToCamelCase = <T>(obj: T): T => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce<Record<string, any>>((result, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );

    const _key = key as keyof T;

    const value =
      typeof obj[_key] === 'object' && obj[_key] !== null
        ? snakeToCamelCase(obj[_key])
        : obj[_key];

    result[camelKey] = value;
    return result;
  }, {}) as T;
};
